/**
 * Formatting shared by the request history table and the phone tiles on My Requests.
 */
import type { ProviderChange } from "../../api/requestsAdmin";

/**
 * The plain-language name of a request type.
 *
 * @param type - The request type.
 * @param forRequester - Names it for the person the request is about rather than for an administrator: a manual vendor
 * task is, to them, a step an administrator takes on their behalf.
 */
export function typeLabel(type: string, forRequester = false): string {
  switch (type) {
    case "ProviderReallocation":
      return "Reallocate AI tool allowance";
    case "ProviderDisable":
      return "Switch off an AI tool";
    case "ProviderEnable":
      return "Switch on an AI tool";
    case "ManualVendorAction":
      return forRequester ? "Set up by an administrator" : "Manual vendor task";
    case "JoinGroup":
      return "Join a team";
    default:
      return type;
  }
}

/** An approved request still waiting for a manual task it depends on (CON-34). */
export const AwaitingDependenciesStatus = "AwaitingDependencies";

/**
 * Whether a request is still open for the person who raised it: waiting for a decision, or approved but waiting for an
 * administrator to set something up.
 */
export function isOpenRequest(status: string): boolean {
  return status === "Pending" || status === AwaitingDependenciesStatus;
}

/** The plain-language name of a request status. */
export function statusLabel(status: string): string {
  return status === AwaitingDependenciesStatus ? "Approved, being set up" : status;
}

/** A timestamp in the reader's own date and time format, or a dash for none. */
export function formatDate(value: string | null): string {
  if (value === null) {
    return "—";
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

const RelativeUnits: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 24 * 60 * 60 * 1000],
  ["month", 30 * 24 * 60 * 60 * 1000],
  ["week", 7 * 24 * 60 * 60 * 1000],
  ["day", 24 * 60 * 60 * 1000],
  ["hour", 60 * 60 * 1000],
  ["minute", 60 * 1000],
];

/**
 * How long ago a timestamp was, in the reader's language ("2 days ago", "yesterday"), for a line that is read at a glance. The exact
 * time belongs in the request's timeline.
 *
 * @param value - The timestamp.
 * @param now - The current time in milliseconds; a parameter so a test does not depend on the date.
 */
export function formatRelative(value: string, now: number = Date.now()): string {
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) {
    return value;
  }

  const elapsed = then - now;
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  for (const [unit, size] of RelativeUnits) {
    if (Math.abs(elapsed) >= size) {
      return formatter.format(Math.trunc(elapsed / size), unit);
    }
  }

  return "just now";
}

/**
 * A person as they are written in the portal: every employee's address ends in the same domain, so it is dropped when a person is named by
 * their address.
 *
 * @param email - The person's email address, or null.
 */
export function shortEmail(email: string | null | undefined): string | null {
  return email === null || email === undefined ? null : email.replace(/@rws\.com$/i, "");
}

/**
 * Whether a provider's setting changes. A provider that was off and stays off is not a change, even though its
 * cells read differently. An unknown before setting is not treated as a change either, since nothing shows what it was.
 *
 * @param change - The provider with its before and after setting.
 * @returns True when the provider is switched on or off, or its limit moves while it stays on.
 */
export function isChanged(change: ProviderChange): boolean {
  if (change.beforeEnabled === null) {
    return false;
  }

  if (change.beforeEnabled !== change.afterEnabled) {
    return true;
  }

  return change.afterEnabled && (change.beforeAmountUsd ?? 0) !== change.afterAmountUsd;
}
